import { Directive, Input, OnInit, Host, ChangeDetectorRef, NgZone } from '@angular/core';
import { NgxMatDatetimepicker } from '@angular-material-components/datetime-picker';
import * as moment from 'moment';

/**
 * Directive to apply default time to ngx-mat-datetime-picker when form value is null
 * 
 * Usage:
 * <ngx-mat-datetime-picker 
 *   #picker 
 *   hmtDefaultTime
 *   [defaultTimeValue]="[14, 30]">
 * </ngx-mat-datetime-picker>
 * 
 * Or use current time:
 * <ngx-mat-datetime-picker 
 *   #picker 
 *   hmtDefaultTime
 *   [useCurrentTime]="true">
 * </ngx-mat-datetime-picker>
 */
@Directive({
    selector: 'ngx-mat-datetime-picker[hmtDefaultTime]',
    standalone: false
})
export class DatetimePickerDefaultTimeDirective implements OnInit {
  @Input() defaultTimeValue: number[] = []; // [hour, minute, second]
  @Input() useCurrentTime = false;

  constructor(
    @Host() private picker: NgxMatDatetimepicker<any>,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    // Store the original open method
    const originalOpen = this.picker.open.bind(this.picker);

    // Override the open method
    this.picker.open = () => {
      // Call original open
      originalOpen();

      // Apply default time with retry mechanism to ensure both form and display are updated
      this.applyDefaultTimeWithRetry();
    };
  }

  private applyDefaultTimeWithRetry(attempt = 0, maxAttempts = 10): void {
    const inputControl = this.picker.datepickerInput as any;
    
    if (!inputControl) {
      if (attempt < maxAttempts) {
        setTimeout(() => this.applyDefaultTimeWithRetry(attempt + 1, maxAttempts), 50);
      }
      return;
    }

    // Check if form already has a value
    const currentValue = inputControl.getStartValue ? inputControl.getStartValue() : 
                        (inputControl.ngControl && inputControl.ngControl.value) ||
                        inputControl.value;
    
    if (currentValue) {
      return; // Already has a value
    }

    // Prepare default date
    const now = moment();
    let defaultDate: Date;

    if (this.useCurrentTime) {
      defaultDate = now.toDate();
    } else if (this.defaultTimeValue && this.defaultTimeValue.length >= 2) {
      defaultDate = now
        .startOf('day')
        .hour(this.defaultTimeValue[0] || 0)
        .minute(this.defaultTimeValue[1] || 0)
        .second(this.defaultTimeValue[2] || 0)
        .toDate();
    } else {
      defaultDate = now.toDate();
    }

    // Method 1: Update model selection (updates both form and picker display)
    if (inputControl._model && inputControl._model.updateSelection) {
      try {
        inputControl._model.updateSelection(defaultDate, inputControl);
      } catch (e) {
        // Continue to other methods
      }
    }

    // Method 2: Update form control directly
    if (inputControl.ngControl && inputControl.ngControl.control) {
      try {
        inputControl.ngControl.control.setValue(defaultDate, { emitEvent: true });
      } catch (e) {
        // Continue
      }
    }

    // Method 3: Write value to input (triggers display update)
    if (inputControl.writeValue) {
      try {
        inputControl.writeValue(defaultDate);
      } catch (e) {
        // Continue
      }
    }

    // Method 4: Update timepicker component directly if available (with delay for initialization)
    // The datepicker may have a timepicker component that needs updating
    setTimeout(() => {
      this.ngZone.run(() => {
        const pickerComponent = (this.picker as any)._componentRef || 
                               (this.picker as any).componentRef ||
                               (this.picker as any)._popupComponentRef;
        if (pickerComponent) {
          const instance = pickerComponent.instance;
          if (instance) {
            // Try to update time property
            if (instance.time !== undefined) {
              try {
                instance.time = {
                  hour: defaultDate.getHours(),
                  minute: defaultDate.getMinutes(),
                  second: defaultDate.getSeconds()
                };
              } catch (e) {
                // Ignore
              }
            }
            // Try to update selected property
            if (instance.selected !== undefined) {
              try {
                instance.selected = defaultDate;
              } catch (e) {
                // Ignore
              }
            }
            // Try to update date property
            if (instance.date !== undefined) {
              try {
                instance.date = defaultDate;
              } catch (e) {
                // Ignore
              }
            }
            // Trigger change detection if available
            if (instance._changeDetectorRef) {
              instance._changeDetectorRef.markForCheck();
            } else if (instance.cdr) {
              instance.cdr.markForCheck();
            }
            // Try to call update method if available
            if (typeof instance.updateTime === 'function') {
              try {
                instance.updateTime(defaultDate.getHours(), defaultDate.getMinutes(), defaultDate.getSeconds());
              } catch (e) {
                // Ignore
              }
            }
          }
        }
        
        // Also try to access via datepicker's internal calendar
        const calendar = (this.picker as any)._calendar;
        if (calendar && calendar.selected) {
          try {
            calendar.selected = defaultDate;
            if (calendar._changeDetectorRef) {
              calendar._changeDetectorRef.markForCheck();
            }
          } catch (e) {
            // Ignore
          }
        }
      });
    }, 150); // Delay to ensure timepicker component is initialized

    // Method 5: Force update via dateChange event
    if (inputControl._onChange) {
      try {
        inputControl._onChange(defaultDate);
      } catch (e) {
        // Continue
      }
    }

    // Method 6: Trigger change callbacks
    if (inputControl._cvaOnChange) {
      try {
        inputControl._cvaOnChange(defaultDate);
      } catch (e) {
        // Continue
      }
    }

    // Method 7: Mark as touched
    if (inputControl._onTouched) {
      try {
        inputControl._onTouched();
      } catch (e) {
        // Continue
      }
    }

    // Force change detection to update UI
    this.ngZone.run(() => {
      this.cdr.markForCheck();
    });

    // Verify the value was set and display updated, retry if needed
    setTimeout(() => {
      const verifyValue = inputControl.getStartValue ? inputControl.getStartValue() : 
                         (inputControl.ngControl && inputControl.ngControl.value);
      if (!verifyValue && attempt < maxAttempts) {
        this.applyDefaultTimeWithRetry(attempt + 1, maxAttempts);
      }
    }, 100);
  }
}

